import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import {
  Popover,
  Modal,
  Button,
  BlockStack,
  InlineStack,
  Select,
  Spinner,
  Text,
} from "@shopify/polaris";
import { FilterIcon } from "@shopify/polaris-icons";
import { useTranslation } from "react-i18next";
import DatePicker from "features/datepicker/date-picker";
import UserFilterSelect from "components/user-filter-select/UserFilterSelect";
import CustomerSelect from "components/customer-select/CustomerSelect";
import useIsPhone from "features/files/use-is-phone";
import StickyFormFooter from "components/sticky-form-footer/sticky-form-footer";
import datePickerValueToString from "./datePickerValueToString";

export interface DocumentFiltersValues {
  dateFrom: string;
  dateTo: string;
  filterUserId: string;
  filterCustomerId: string;
  filterSigned: string;
}

const emptyFilters: DocumentFiltersValues = {
  dateFrom: "",
  dateTo: "",
  filterUserId: "",
  filterCustomerId: "",
  filterSigned: "",
};

interface DocumentFiltersPopoverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  appliedFilters: DocumentFiltersValues;
  onApplyFilters: (filters: DocumentFiltersValues) => void;
  hasActiveFilters: boolean;
}

const PREPARE_DELAY_MS = 280;

// The app scrolls inside <main>, not the window.
const pageScroller = () => document.querySelector<HTMLElement>("main.dashboard-main");

/**
 * Filter form content. Mounted only when popover (on a phone: dialog) is open; initial state is
 * appliedFilters. When there are applied user/customer filters, we show a
 * short "Preparing…" state so UserFilterSelect/CustomerSelect can resolve
 * labels before the form is shown (avoids blink).
 */
function FilterPopoverContent({
  appliedFilters,
  onApply,
}: {
  appliedFilters: DocumentFiltersValues;
  onApply: (filters: DocumentFiltersValues) => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<DocumentFiltersValues>(appliedFilters);
  const hasAnyApplied =
    Boolean(appliedFilters.dateFrom) ||
    Boolean(appliedFilters.dateTo) ||
    Boolean(appliedFilters.filterUserId) ||
    Boolean(appliedFilters.filterCustomerId) ||
    Boolean(appliedFilters.filterSigned);
  const [contentReady, setContentReady] = useState(!hasAnyApplied);

  useEffect(() => {
    if (!hasAnyApplied) return;
    const id = window.setTimeout(() => setContentReady(true), PREPARE_DELAY_MS);
    return () => window.clearTimeout(id);
  }, [hasAnyApplied]);

  const handleApply = () => {
    onApply(draft);
  };

  const handleClearAll = () => {
    setDraft(emptyFilters);
    onApply(emptyFilters);
  };

  if (!contentReady) {
    return (
      <div className="p-4 min-w-[320px] max-md:p-0 max-md:min-w-0 flex flex-col items-center justify-center gap-3 min-h-[200px]">
        <Spinner size="large" />
        <Text as="p" variant="bodySm" tone="subdued">
          {t("preparing_filters") !== "preparing_filters"
            ? t("preparing_filters")
            : "Preparing filters…"}
        </Text>
      </div>
    );
  }

  return (
    <div className="p-4 min-w-[320px] max-md:p-0 max-md:min-w-0">
      <BlockStack gap="400">
        <DatePicker
          title={t("date_from")}
          selected={draft.dateFrom}
          onChange={(value) =>
            setDraft((p) => ({ ...p, dateFrom: datePickerValueToString(value) }))
          }
          isRequired={false}
          disableDatesAfter=""
        />
        <DatePicker
          title={t("date_to")}
          selected={draft.dateTo}
          onChange={(value) =>
            setDraft((p) => ({ ...p, dateTo: datePickerValueToString(value) }))
          }
          isRequired={false}
          disableDatesAfter=""
        />
        <UserFilterSelect
          value={draft.filterUserId}
          onChange={(v) => setDraft((p) => ({ ...p, filterUserId: v }))}
          label={t("user")}
        />
        <CustomerSelect
          allowMultiple={false}
          value={draft.filterCustomerId}
          onChange={(v: string | string[]) =>
            setDraft((p) => ({
              ...p,
              filterCustomerId: Array.isArray(v) ? v[0] ?? "" : v ?? "",
            }))
          }
          isRequired={false}
          label={t("customer")}
        />
        <Select
          label={t("status")}
          options={[
            { label: t("all") || "All", value: "" },
            { label: t("signed") || "Signed", value: "signed" },
            {
              label: t("not_signed") || "Not signed",
              value: "unsigned",
            },
          ]}
          value={draft.filterSigned}
          onChange={(v) => setDraft((p) => ({ ...p, filterSigned: v }))}
        />
      </BlockStack>
      <StickyFormFooter>
        <InlineStack gap="200" blockAlign="end">
          <Button variant="plain" onClick={handleClearAll}>
            {t("clear") !== "clear" ? t("clear") : "Clear"}
          </Button>
          <Button variant="primary" onClick={handleApply}>
            {t("apply_filters") !== "apply_filters"
              ? t("apply_filters")
              : "Apply filters"}
          </Button>
        </InlineStack>
      </StickyFormFooter>
    </div>
  );
}

export default function DocumentFiltersPopover({
  open,
  onOpenChange,
  appliedFilters,
  onApplyFilters,
  hasActiveFilters,
}: DocumentFiltersPopoverProps) {
  const { t } = useTranslation();

  const handleApply = (filters: DocumentFiltersValues) => {
    onApplyFilters(filters);
    onOpenChange(false);
  };

  const isPhone = useIsPhone();

  // Polaris locks the page while the dialog is open and, on closing, puts back only the window's scroll, so the
  // list under <main> would jump. Remember where <main> was before the dialog and return there once Polaris is done
  // (a passive effect runs after the cleanup of the unmounted dialog backdrop).
  const isDialogOpen = isPhone && open;
  const scrollTopBeforeDialog = useRef<number | null>(null);
  useLayoutEffect(() => {
    if (isDialogOpen) scrollTopBeforeDialog.current = pageScroller()?.scrollTop ?? null;
  }, [isDialogOpen]);
  useEffect(() => {
    const scrollTop = scrollTopBeforeDialog.current;
    if (isDialogOpen || scrollTop === null) return;
    scrollTopBeforeDialog.current = null;
    const scroller = pageScroller();
    if (scroller) scroller.scrollTop = scrollTop;
  }, [isDialogOpen]);

  const filterButton = (
    <Button
      icon={FilterIcon as any}
      onClick={() => onOpenChange(!open)}
      variant={hasActiveFilters ? "primary" : "secondary"}
    >
      {t("filters") !== "filters" ? t("filters") : "Filter"}
    </Button>
  );

  const filterContent = open && (
    <FilterPopoverContent
      key="filter-content"
      appliedFilters={appliedFilters}
      onApply={handleApply}
    />
  );

  // A small popover with nested pop-ups is too tight for a phone (and the
  // keyboard hides its buttons), so there the same form opens as a dialog.
  if (isPhone) {
    return (
      <>
        {filterButton}
        <Modal
          open={open}
          onClose={() => onOpenChange(false)}
          title={t("filters") !== "filters" ? t("filters") : "Filter"}
        >
          <Modal.Section>{filterContent}</Modal.Section>
        </Modal>
      </>
    );
  }

  return (
    <Popover
      active={open}
      activator={filterButton}
      onClose={() => onOpenChange(false)}
      preferredAlignment="right"
      preferredPosition="below"
      preventCloseOnChildOverlayClick
    >
      {filterContent}
    </Popover>
  );
}
